import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * POPIA consent gate. Blocks the entire authenticated UI behind a mandatory
 * consent acceptance modal until the user has accepted the current policy
 * + terms versions. Optional info-sharing consent is also presented but
 * not required.
 */
export default function ConsentGate({ children }) {
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [required, setRequired] = useState(false);
  const [popiaChecked, setPopiaChecked] = useState(false);
  const [shareChecked, setShareChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [policyVersion, setPolicyVersion] = useState(null);
  const [termsVersion, setTermsVersion] = useState(null);

  useEffect(() => {
    if (!user?.user_id) {
      setChecking(false);
      setRequired(false);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const r = await fetch(`${API_URL}/api/consent/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!r.ok) {
          // Don't lock users out if the consent service is unavailable
          if (!cancelled) { setRequired(false); setChecking(false); }
          return;
        }
        const d = await r.json();
        if (cancelled) return;
        setPolicyVersion(d.current_policy_version);
        setTermsVersion(d.current_terms_version);
        setRequired(!!d.required);
        setShareChecked(!!d.info_sharing?.accepted);
        setChecking(false);
      } catch {
        if (!cancelled) { setRequired(false); setChecking(false); }
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  const platform = (() => {
    if (typeof window === 'undefined') return 'web';
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua)) return 'android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    return 'web';
  })();

  const handleAccept = async () => {
    if (!popiaChecked) {
      toast.error('Please accept the POPIA consent to continue');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      const popiaRes = await fetch(`${API_URL}/api/consent/popia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ platform, policy_version: policyVersion, terms_version: termsVersion }),
      });
      if (!popiaRes.ok) {
        const e = await popiaRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not record consent');
      }
      // Optional info-sharing consent — best-effort; if it fails we still dismiss the gate
      // because POPIA was recorded, but warn the user so they know to retry from Profile.
      try {
        const shareRes = await fetch(`${API_URL}/api/consent/info-sharing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ accepted: shareChecked, platform }),
        });
        if (!shareRes.ok) throw new Error();
      } catch {
        toast.warning('Information-sharing preference could not be saved. You can set it in Profile > Privacy.');
      }
      toast.success('Thank you — your consent has been recorded.');
      setRequired(false);
    } catch (err) {
      toast.error(err?.message || 'Could not save your consent — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // While we're checking on first load, render children so the rest of the
  // app paints. The gate appears as soon as we know it's required.
  if (checking) return children;
  if (!required) return children;

  return (
    <>
      {/* Underlying app stays mounted to avoid mount/unmount churn */}
      {children}
      <div
        className="fixed inset-0 z-[1000] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        data-testid="consent-gate-overlay"
      >
        <Card className="max-w-xl w-full" data-testid="consent-gate-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <CardTitle>Before you continue</CardTitle>
                <CardDescription>
                  Please review and accept our updated POPIA notice. This is required to use BodyCorp365.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-slate-600">
              By creating an account or continuing to use BodyCorp365 you acknowledge that you have read and understood the{' '}
              <Link to="/privacy-policy" target="_blank" className="text-sky-600 underline" data-testid="link-privacy-policy">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/terms-of-service" target="_blank" className="text-sky-600 underline" data-testid="link-terms">
                Terms &amp; Conditions
              </Link>
              .
            </p>

            <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <Checkbox
                id="popia-consent"
                checked={popiaChecked}
                onCheckedChange={setPopiaChecked}
                data-testid="popia-consent-checkbox"
                className="mt-1"
              />
              <Label htmlFor="popia-consent" className="text-sm leading-relaxed text-slate-800 cursor-pointer">
                <strong>I consent to Edutainment News T/A BodyCorp365 collecting, processing, storing and using my personal information</strong> in accordance with the Protection of Personal Information Act (POPIA), the BodyCorp365 Privacy Policy and Terms and Conditions.
                <span className="block text-xs text-red-600 mt-1">Required</span>
              </Label>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
              <Checkbox
                id="info-sharing-consent"
                checked={shareChecked}
                onCheckedChange={setShareChecked}
                data-testid="info-sharing-consent-checkbox"
                className="mt-1"
              />
              <Label htmlFor="info-sharing-consent" className="text-sm leading-relaxed text-slate-700 cursor-pointer">
                I consent to BodyCorp365 sharing my personal information, where reasonably necessary, with my Management Company, Insurance Company and authorised suppliers or service providers for the purposes of administering my community, processing maintenance requests, insurance claims and related services.
                <span className="block text-xs text-slate-500 mt-1">Optional — you can change this any time from Profile &gt; Privacy</span>
              </Label>
            </div>

            <Button
              onClick={handleAccept}
              disabled={!popiaChecked || submitting}
              className="w-full"
              data-testid="accept-consent-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Accept &amp; continue
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
